import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';
import { useDataProvider, useNotify, useTranslate } from 'ra-core';
import { type AdminDataProvider } from '../../adminDataProvider';

type LeaveChannelParams = {
  channelUrl: string;
};

export const useLeaveChannel = (
  options?: UseMutationOptions<void, Error, LeaveChannelParams>
) => {
  const notify = useNotify();
  const translate = useTranslate();
  const dataProvider = useDataProvider<AdminDataProvider>();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['leave-channel'],
    mutationFn: ({ channelUrl }: LeaveChannelParams) =>
      dataProvider.postLeaveChannel(channelUrl),
    ...options,
    onSuccess: async (data, variables, onMutateResult, context) => {
      await Promise.all([
        // Invalidate all channel member queries so lists refresh after a leave
        queryClient.invalidateQueries({
          queryKey: ['channel-members'],
        }),
      ]);

      await options?.onSuccess?.(data, variables, onMutateResult, context);
    },
    onError: async (error, variables, onMutateResult, context) => {
      notify(translate('chat.leaveChannelError'), {
        autoHideDuration: 5000,
      });
      await options?.onError?.(error, variables, onMutateResult, context);
    },
  });
};
